import { Checkbox } from '@ophelos/ui/components/checkbox';
import { Label } from '@ophelos/ui/components/label';
import { useTranslation } from '@/hooks/useTranslation';
import { usePaymentMethodAvailability } from '@/hooks/usePaymentMethodAvailability';
import type { PaymentMethod } from '@ophelos/shared/constants/payment-methods';
import { paymentMethods } from './payment-method-constants';

interface PaymentMethodSelectionSimpleProps {
  selectedMethod: PaymentMethod | null;
  onSelect: (method: PaymentMethod) => void;
  disabled?: boolean;
  availableMethods?: PaymentMethod[];
  /** Methods present in the list but not yet selectable — shown greyed out
   *  with the given reason instead of being hidden (e.g. a lead-time floor
   *  the picked date hasn't cleared). */
  disabledReasons?: Partial<Record<PaymentMethod, string>>;
}

export function PaymentMethodSelectionSimple({
  selectedMethod,
  onSelect,
  disabled,
  availableMethods,
  disabledReasons,
}: PaymentMethodSelectionSimpleProps) {
  const { t } = useTranslation();
  const { data: availability, isLoading } = usePaymentMethodAvailability();

  const isDisabled = disabled || isLoading;

  const methods = paymentMethods
    .filter((m) => availability?.[m.value] ?? true)
    .filter((m) => !availableMethods || availableMethods.includes(m.value));

  return (
    <div className="flex flex-col gap-4 w-full">
      {methods.map((method) => {
        const Icon = method.icon;
        const isSelected = selectedMethod === method.value;
        const reason = disabledReasons?.[method.value];
        const rowDisabled = isDisabled || !!reason;

        return (
          <div
            key={method.value}
            className={`group overflow-hidden flex items-center space-x-4 transition-all border rounded-2xl p-3 ${
              isSelected ? 'border-ring/50 bg-accent/15 shadow-lg/5' : 'bg-card'
            } ${rowDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            onClick={() => !rowDisabled && onSelect(method.value)}
          >
            <Label htmlFor={`payment-method-${method.value}`} className="flex-1 cursor-pointer text-sm flex items-center gap-6">
              <div
                className={`flex items-center justify-center size-8 ${
                  isSelected && '-rotate-5'
                } transition-transform duration-300 shrink-0 -ml-2 p-2 ${isSelected ? 'grayscale-0 opacity-60' : 'grayscale opacity-20'}`}
              >
                <div className="p-3.5 border border-ring/8 rounded-[1.5rem] bg-accent/20">
                  <div className="p-2.5 border border-ring/15 rounded-[1rem] shadow-lg shadow-ring/10 bg-accent/35">
                    <div className="p-2 border border-ring/30 rounded-[0.625rem] shadow-lg shadow-ring/20 bg-card/75">
                      <Icon className="size-3 text-accent-foreground" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-start">
                <span className="font-semibold text-base">{t(method.labelKey)}</span>
                <span className="text-sm text-muted-foreground text-center">{reason ?? t(method.descriptionKey)}</span>
              </div>
            </Label>
            <Checkbox
              checked={isSelected}
              id={`payment-method-${method.value}`}
              disabled={rowDisabled}
              onCheckedChange={() => !rowDisabled && onSelect(method.value)}
            />
          </div>
        );
      })}
    </div>
  );
}
